import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Header, PopUpNav, SubmitButton } from '../../../../components';
import { Page } from '../../../../components/page';
import { useInstallationContext } from '../../../../context/installation';
import { DashboardSections } from '../../../../data-access/gql-types/graphql';
import { isEkoOzeDashboardDemo, isWeatherDashboardDemo } from '../../../../utils/helpers/demo';
import CheckContentWrapper from '../common/check-content-wrapper';
import { DashboardPartVisibility } from './components/dashboard-part-visibility/dashboard-part-visibility';
import { DashboardContextProvider } from './context/dashboard-context';
import { useDashboard } from './hooks/use-dashboard';
import { DashboardActions } from './sections/actions/actions';
import { DashboardEcoOze } from './sections/eco-oze/eco-oze';
import { DashboardGreetingsBanner } from './sections/greetings/greetings';
import { DashboardMeasurements } from './sections/measurements/measurements';
import { DashboardResources } from './sections/resources/resources';
import { DashboardTariff } from './sections/tariff/tariff';
import { DashboardWeatherWidget } from './sections/weather-widget/weather-widget';
import { DashboardWhatsNew } from './sections/whats-new/whats-new';

const Dashboard: React.FC = () => {
  const { t } = useTranslation('common');
  const { selectedInstallationId } = useInstallationContext();
  const { visibleMode, navLinks, visibilitySections, toggleVisible, handleSaveVisibilitySections } = useDashboard();

  const renderComponent = (component: React.ReactNode, elementPart: DashboardSections) => {
    const sectionShouldRender = visibleMode || visibilitySections[elementPart];
    if (!sectionShouldRender) return null;

    return (
      <DashboardPartVisibility
        visibleMode={visibleMode}
        isVisible={visibilitySections[elementPart]}
        onToggle={() => toggleVisible(elementPart)}
      >
        {component}
      </DashboardPartVisibility>
    );
  };

  return (
    <Page
      className="dashboard"
      isStickyHeader
      containerClassName={classNames('page__container--flex-height p-t-0', { 'p-r-0 p-l-0': selectedInstallationId })}
      header={
        <Header title={t('navigation.dashboard')}>
          <PopUpNav links={navLinks} />
        </Header>
      }
    >
      <CheckContentWrapper>
        <DashboardContextProvider>
          {renderComponent(<DashboardGreetingsBanner />, DashboardSections.Greetings)}
          {renderComponent(<DashboardTariff />, DashboardSections.TariffSimulation)}
          {isWeatherDashboardDemo && renderComponent(<DashboardWeatherWidget />, DashboardSections.WeatherWidget)}
          {renderComponent(<DashboardActions />, DashboardSections.QuickActions)}
          {renderComponent(<DashboardMeasurements />, DashboardSections.MeterData)}
          {isEkoOzeDashboardDemo && renderComponent(<DashboardEcoOze />, DashboardSections.Pv)}
          {renderComponent(<DashboardResources />, DashboardSections.Resources)}
          {renderComponent(<DashboardWhatsNew visibleMode={visibleMode} />, DashboardSections.WhatsNew)}
        </DashboardContextProvider>
      </CheckContentWrapper>

      {visibleMode && <SubmitButton onClick={handleSaveVisibilitySections} />}
    </Page>
  );
};

export default Dashboard;
